import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";
import "../styles/night-legal.css";

const updatedAt = "June 27, 2026";

const sections = [
  {
    title: "Acceptance and account responsibility",
    body: [
      "By accessing BNE Studio, creating an account, submitting content, or using any distribution, reporting, payout, contract, link, event, or creative tool, you agree to these terms and any additional terms presented for a specific product or workflow.",
      "You are responsible for keeping your account, tax, identity, payment, contact, artist, label, and catalog information accurate and current. You are also responsible for all activity under your account and for protecting your login credentials.",
      "You may not use BNE Studio to violate law, infringe rights, impersonate another person or artist, misrepresent ownership, manipulate streams or engagement, upload malicious material, interfere with the platform, or access systems or data without authorization.",
    ],
  },
  {
    title: "Submitted content and ownership",
    body: [
      "You keep ownership of the recordings, compositions, artwork, metadata, lyrics, videos, files, event materials, links, names, likenesses, trademarks, and other content you submit, subject to the licenses and permissions needed for BNE Studio to provide the requested services.",
      "You represent and warrant that you control or have obtained all rights, licenses, consents, clearances, and permissions needed for every item you submit, including sound recording rights, composition rights, artwork rights, name and likeness rights, samples, beats, stems, remixes, features, producer contributions, public domain claims, and any third-party material.",
      "You are responsible for the accuracy of all metadata, credits, splits, contributors, artist names, label names, ownership percentages, release dates, territories, explicit-content flags, publisher information, ISRCs, UPCs, and other catalog information.",
    ],
  },
  {
    title: "Distribution license",
    body: [
      "When you submit content for distribution, you grant BNE Studio a worldwide, non-exclusive right and license to reproduce, encode, store, host, format, transmit, publicly perform, publicly display, distribute, monetize, promote, and otherwise use the submitted content, metadata, artwork, and related materials solely as needed to provide, administer, and improve the requested services.",
      "This license includes the right to deliver content to digital service providers, stores, streaming platforms, social platforms, content identification systems, licensing partners, payment providers, analytics providers, and other distribution or operational partners.",
      "This license also includes the right to create technical copies, previews, clips, thumbnails, metadata packages, reporting records, identifiers, and other delivery assets required by partners or platform operations.",
      "The distribution license continues for as long as needed to provide services, collect and account for revenue, process takedowns, preserve business records, resolve disputes, comply with law, and administer rights, reporting, or payment obligations.",
    ],
  },
  {
    title: "Store and partner rules",
    body: [
      "Distribution depends on third-party stores, streaming services, social platforms, collection partners, payment providers, and other vendors. Their rules, review processes, territory restrictions, content standards, technical requirements, fraud policies, and reporting timelines may apply to your content.",
      "BNE Studio may reject, delay, edit formatting for, suspend, or remove content that appears incomplete, inaccurate, infringing, fraudulent, abusive, misleading, low quality, duplicative, noncompliant, or otherwise risky to users, partners, stores, rights holders, or BNE Studio.",
      "Stores and partners may reject, hide, demonetize, remove, downgrade, flag, or modify content availability without notice. BNE Studio is not responsible for partner decisions, delays, editorial choices, search placement, playlisting, algorithmic treatment, store outages, or platform policy changes.",
    ],
  },
  {
    title: "Royalties, revenue, and payment",
    body: [
      "Royalties and other revenue are calculated from amounts actually received by BNE Studio or its applicable partners for your content, less applicable fees, commissions, taxes, refunds, chargebacks, reserves, adjustments, payment processing costs, currency conversion costs, bank fees, partner deductions, recoupments, or amounts owed under another agreement.",
      "Reporting and payment timing depend on store and partner reports, payment cycles, data quality, territory, currency, banking information, tax status, fraud review, and account verification. Reports may be delayed, corrected, reversed, or adjusted.",
      "You are responsible for providing accurate payout, tax, identity, and banking information. BNE Studio may withhold, delay, offset, or reserve payments when information is missing, a dispute exists, law requires it, fraud or abnormal activity is suspected, partner reports are pending, or amounts may be subject to chargeback, refund, recoupment, or third-party claim.",
      "If BNE Studio pays an amount in error, receives a reversal, or later determines that amounts were not payable, BNE Studio may deduct, offset, recoup, invoice, or otherwise recover those amounts from current or future payments.",
    ],
  },
  {
    title: "Splits and collaborators",
    body: [
      "If you use split, collaborator, recoupment, team, contract, or payout features, you are responsible for entering accurate recipients, percentages, roles, payment instructions, and ownership information.",
      "BNE Studio may rely on the split and payout instructions provided through your account. We are not responsible for disputes between collaborators, labels, artists, producers, writers, managers, publishers, or other third parties.",
      "If a dispute arises, BNE Studio may pause distributions, payments, account access, metadata changes, split changes, or takedowns until the parties resolve the issue or provide documentation BNE Studio considers sufficient.",
    ],
  },
  {
    title: "Takedowns, corrections, and catalog control",
    body: [
      "You may request takedowns or metadata corrections through available tools or support channels. Processing time depends on BNE Studio, stores, partners, technical systems, and the nature of the request.",
      "BNE Studio may remove or disable content at any time if required by law, partner policy, rights-owner request, fraud review, account status, nonpayment, risk management, or these terms.",
      "After a takedown, stores and partners may retain archival, reporting, accounting, playlist, cache, legal, or compliance records. Prior usage, reports, claims, and payment adjustments may continue after removal.",
    ],
  },
  {
    title: "Fraud, artificial activity, and abuse",
    body: [
      "You may not create, purchase, encourage, or benefit from artificial streams, fake saves, bot traffic, click farms, abnormal engagement, misleading promotion, unauthorized advertising claims, manipulated charts, fraudulent purchases, or other activity intended to deceive stores, fans, partners, or BNE Studio.",
      "If fraud, manipulation, infringement, abnormal activity, or policy abuse is suspected, BNE Studio may investigate, withhold revenue, remove content, suspend accounts, reverse payments, close accounts, report activity to partners, and provide relevant information to stores, payment providers, rights holders, or legal authorities.",
      "Stores and partners may impose penalties, deductions, bans, takedowns, or revenue forfeitures for artificial activity or other violations. You are responsible for losses, fees, claims, or penalties connected to your content or account activity.",
    ],
  },
  {
    title: "Copyright, disputes, and claims",
    body: [
      "BNE Studio may respond to copyright notices, trademark claims, right-of-publicity claims, ownership disputes, impersonation claims, fraud reports, and other rights complaints by removing or restricting content, requesting documentation, sharing information with affected parties, or suspending related payments.",
      "You must promptly provide documentation requested to verify ownership, licenses, samples, cover-song rights, producer rights, performer rights, artwork rights, publishing information, or authority to act for an artist, label, company, or rights holder.",
      "You agree to cooperate with BNE Studio in resolving claims and to reimburse BNE Studio for losses, costs, partner penalties, chargebacks, legal fees, or liabilities arising from your content, rights, account, instructions, or breach of these terms.",
    ],
  },
  {
    title: "Cover songs, samples, remixes, and third-party material",
    body: [
      "You are responsible for ensuring that cover songs, samples, interpolations, beats, loops, remixes, translations, public domain works, stems, artwork, videos, names, trademarks, and other third-party materials are properly cleared before submission.",
      "Some uses may require mechanical licenses, synchronization licenses, master-use licenses, sample clearances, publisher approvals, performer consents, producer approvals, union compliance, or territory-specific permissions. BNE Studio does not guarantee that a submission is cleared or legally compliant.",
      "BNE Studio may reject, delay, restrict, or remove content that appears to include uncleared or disputed material.",
    ],
  },
  {
    title: "Fees, subscriptions, and billing",
    body: [
      "Some BNE Studio features may require fees, subscriptions, commissions, revenue shares, processing costs, or other charges disclosed in the product, checkout, order flow, or a separate agreement.",
      "You are responsible for applicable taxes, payment-method accuracy, renewals, failed payments, bank fees, and any costs associated with your account or requested services.",
      "If payment fails, an account lapses, or amounts remain unpaid, BNE Studio may limit access, pause services, remove content, withhold payments, or require payment before continuing services.",
    ],
  },
  {
    title: "Service availability and changes",
    body: [
      "We may update, suspend, remove, limit, or change features, pricing, workflows, partners, eligibility requirements, and technical systems as the platform evolves.",
      "We work to keep services available, but do not guarantee uninterrupted, secure, or error-free operation. Planned or emergency maintenance, partner outages, internet issues, security events, and platform changes may affect availability.",
    ],
  },
  {
    title: "Privacy and communications",
    body: [
      "Use of BNE Studio is also governed by the Privacy Policy. By using the platform, you consent to communications related to your account, security, service updates, payments, support, rights issues, and policy changes.",
      "You are responsible for monitoring the email address and account notifications connected to your account.",
    ],
  },
  {
    title: "Indemnification",
    body: [
      "You agree to defend, indemnify, and hold harmless BNE Studio, its affiliates, service providers, partners, officers, employees, contractors, and agents from claims, damages, losses, liabilities, costs, and expenses, including reasonable attorneys' fees, arising from your content, account activity, rights disputes, payment instructions, fraud, breach of these terms, or violation of law or third-party rights.",
    ],
  },
  {
    title: "Disclaimers and limitation of liability",
    body: [
      "BNE Studio is provided on an as-is and as-available basis. To the fullest extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose, title, non-infringement, accuracy, availability, and uninterrupted operation.",
      "BNE Studio does not guarantee distribution acceptance, store placement, playlisting, monetization, earnings, audience growth, ticket sales, search ranking, reporting timing, payout timing, or any particular commercial outcome.",
      "To the fullest extent permitted by law, BNE Studio will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, lost profits, lost revenue, lost data, business interruption, reputational harm, or partner decisions, even if we have been advised of the possibility of such damages.",
    ],
  },
  {
    title: "Legal, tax, accounting, and rights advice",
    body: [
      "BNE Studio may provide tools, templates, dashboards, reports, or educational information, but nothing on the platform is legal, tax, accounting, financial, publishing, licensing, or rights-clearance advice.",
      "You should consult your own advisors before making legal, tax, financial, rights, ownership, contract, or release decisions.",
    ],
  },
];

export default function TermsPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Terms of Use - BNE Studio";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout>
      <main className="legal-page">
        <header className="legal-hero">
          <div className="wrap">
            <span className="eyebrow dot">Terms</span>
            <h1 className="grad-text">Terms of Use</h1>
            <p>
              These terms govern access to bne.studio and related BNE Studio
              products and services, including music distribution, catalog
              management, reporting, payouts, creator tools, links, contracts,
              and event workflows. Additional product, partner, store, or
              written agreement terms may also apply.
            </p>
            <div className="legal-meta">
              <span>Last updated: {updatedAt}</span>
              <Link to="/privacy">Read the privacy policy</Link>
              <Link to="/distribution-agreement">Distribution agreement</Link>
            </div>
          </div>
        </header>

        <section className="legal-section">
          <div className="wrap legal-grid legal-grid-single">
            <div className="legal-stack">
              {sections.map((section) => (
                <article className="legal-card" key={section.title}>
                  <h3>{section.title}</h3>
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </article>
              ))}

              <article className="legal-card">
                <h3>Termination</h3>
                <p>
                  We may suspend or terminate access if an account violates
                  these terms, creates legal or security risk, abuses the
                  platform, or is required to be restricted by law or partner
                  policy.
                </p>
                <p>
                  Termination does not eliminate payment obligations,
                  indemnities, rights needed to complete takedowns, accounting,
                  dispute handling, partner reporting, tax compliance, or other
                  obligations that by their nature should survive termination.
                </p>
              </article>

              <article className="legal-card">
                <h3>Updates to these terms</h3>
                <p>
                  We may update these terms from time to time. Continued use of
                  the service after updates are posted means the updated terms
                  apply.
                </p>
              </article>

              <article className="legal-card legal-callout">
                <h3>Questions about terms?</h3>
                <p>
                  Reach out if you need help understanding how these terms apply
                  to a BNE Studio product or account.
                </p>
                <Link className="btn btn-primary" to="/contact-us">
                  Contact us
                </Link>
              </article>
            </div>
          </div>
        </section>
      </main>
    </NightLayout>
  );
}
